import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminCertificatePreview,
  adminCertificateRecipients,
  adminSendCertificates,
  type ConferenceCertificateRecipientCustomization,
  type ConferenceCorrespondenceRecipient,
} from "../api";
import { PageHeader } from "../shells/PageHeader";

type RecipientDraft = {
  honorific: string;
  recipient_name: string;
  appreciation: string;
};

const HONORIFIC_OPTIONS = [
  { value: "", labelKey: "certHonorificNone" as const },
  { value: "Sheikh", labelKey: "certHonorificSheikh" as const },
  { value: "Dr.", labelKey: "certHonorificDr" as const },
  { value: "Sheikh Dr.", labelKey: "certHonorificSheikhDr" as const },
  { value: "Mr.", labelKey: "certHonorificMr" as const },
  { value: "الشيخ", labelKey: "certHonorificSheikhAr" as const },
  { value: "الدكتور", labelKey: "certHonorificDrAr" as const },
  { value: "الشيخ الدكتور", labelKey: "certHonorificSheikhDrAr" as const },
  { value: "__custom__", labelKey: "certHonorificCustom" as const },
];

function typeLabel(
  t: ReturnType<typeof useLanguage>["t"],
  recipientType: string,
): string {
  if (recipientType === "registered_reviewer") return t.admin.corrTypeRegisteredReviewer;
  if (recipientType === "external_reviewer") return t.admin.corrTypeExternalReviewer;
  return t.admin.corrTypeParticipant;
}

function draftFromRecipient(row: ConferenceCorrespondenceRecipient): RecipientDraft {
  return {
    honorific: (row.suggested_honorific || row.title_or_role || "").trim(),
    recipient_name: row.name,
    appreciation: "",
  };
}

export function AdminCertificatesPage() {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ConferenceCorrespondenceRecipient[]>([]);
  const [selected, setSelected] = useState<ConferenceCorrespondenceRecipient[]>([]);
  const [drafts, setDrafts] = useState<Record<string, RecipientDraft>>({});
  const [language, setLanguage] = useState<"auto" | "en" | "ar" | "fr">("auto");
  const [notes, setNotes] = useState("");
  const [previewTargetId, setPreviewTargetId] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ recipients?: string }>({});
  const sendLock = useRef(false);
  const alertRef = useRef<HTMLDivElement>(null);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.user_id)), [selected]);
  const busy = previewing || sending;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      adminCertificateRecipients(query.trim() || undefined)
        .then(setResults)
        .catch(() => setResults([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if ((success || error) && alertRef.current) {
      alertRef.current.focus();
    }
  }, [success, error]);

  useEffect(() => {
    if (!selected.length) {
      setPreviewTargetId(null);
      return;
    }
    if (!previewTargetId || !selectedIds.has(previewTargetId)) {
      setPreviewTargetId(selected[0]?.user_id ?? null);
    }
  }, [selected, selectedIds, previewTargetId]);

  const toggle = (row: ConferenceCorrespondenceRecipient) => {
    setSelected((prev) => {
      if (prev.some((p) => p.user_id === row.user_id)) {
        setDrafts((d) => {
          const next = { ...d };
          delete next[row.user_id];
          return next;
        });
        return prev.filter((p) => p.user_id !== row.user_id);
      }
      setDrafts((d) => ({ ...d, [row.user_id]: draftFromRecipient(row) }));
      return [...prev, row];
    });
    setFieldErrors((fe) => ({ ...fe, recipients: undefined }));
  };

  const removeSelected = (userId: string) => {
    setSelected((prev) => prev.filter((p) => p.user_id !== userId));
    setDrafts((d) => {
      const next = { ...d };
      delete next[userId];
      return next;
    });
  };

  const updateDraft = (userId: string, patch: Partial<RecipientDraft>) => {
    setDrafts((d) => {
      const existing = d[userId];
      const row = selected.find((s) => s.user_id === userId);
      const base = existing || (row ? draftFromRecipient(row) : { honorific: "", recipient_name: "", appreciation: "" });
      return { ...d, [userId]: { ...base, ...patch } };
    });
  };

  const buildCustomizations = (): ConferenceCertificateRecipientCustomization[] =>
    selected.map((s) => {
      const draft = drafts[s.user_id] || draftFromRecipient(s);
      const honorific = draft.honorific === "__custom__" ? "" : draft.honorific.trim();
      return {
        user_id: s.user_id,
        honorific: honorific || null,
        recipient_name: draft.recipient_name.trim() || s.name,
        appreciation: draft.appreciation.trim() || null,
      };
    });

  const payload = () => ({
    recipient_user_ids: selected.map((s) => s.user_id),
    language: language === "auto" ? null : language,
    notes: notes.trim() || null,
    preview_recipient_user_id: previewTargetId || selected[0]?.user_id || null,
    customizations: buildCustomizations(),
  });

  const validateRequired = (): boolean => {
    if (!selected.length) {
      setFieldErrors({ recipients: t.admin.certNeedRecipients });
      setError(t.admin.certFixRequired);
      setSuccess(null);
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const resetFormAfterSend = () => {
    setSelected([]);
    setDrafts({});
    setNotes("");
    setLanguage("auto");
    setPreviewTargetId(null);
    setFieldErrors({});
  };

  const onPreview = async () => {
    if (!validateRequired()) return;
    setPreviewing(true);
    setError(null);
    setSuccess(null);
    try {
      const blob = await adminCertificatePreview(payload());
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setSuccess(t.admin.certPreviewReady);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setPreviewing(false);
    }
  };

  const onSend = async () => {
    if (sendLock.current || sending) return;
    if (!validateRequired()) return;
    sendLock.current = true;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminSendCertificates(payload());
      if (result.failed > 0 && result.sent === 0) {
        setError(
          t.admin.certSendAllFailed
            .replace("{failed}", String(result.failed))
            .replace("{total}", String(result.sent + result.failed)),
        );
      } else if (result.failed > 0) {
        setSuccess(null);
        setError(
          t.admin.certSendPartial
            .replace("{sent}", String(result.sent))
            .replace("{failed}", String(result.failed)),
        );
      } else {
        const msg =
          result.sent === 1 && result.items[0]
            ? t.admin.certSendSuccessOne.replace("{name}", result.items[0].recipient_name)
            : t.admin.certSendSuccessMany.replace("{count}", String(result.sent));
        resetFormAfterSend();
        setSuccess(msg);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setSending(false);
      sendLock.current = false;
    }
  };

  return (
    <div className="dn-corr-page">
      <PageHeader
        title={t.admin.certificates}
        lead={t.admin.certificatesLead}
        backTo="/admin"
        actions={
          <Link to="/admin/certificates/history" className="dn-btn dn-btn-ghost">
            {t.admin.certHistory}
          </Link>
        }
      />

      {(error || success) && (
        <div
          ref={alertRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          className={`dn-alert ${error ? "dn-alert-error" : "dn-alert-success"} dn-corr-toast`}
        >
          <div className="dn-corr-toast-body">
            <strong>{error ? t.admin.certNoticeError : t.admin.certNoticeSuccess}</strong>
            <p>{error || success}</p>
            <Link to="/admin/certificates/history" className="dn-corr-toast-link">
              {t.admin.certViewHistory}
            </Link>
          </div>
          <button
            type="button"
            className="dn-btn dn-btn-ghost"
            onClick={() => {
              setError(null);
              setSuccess(null);
            }}
          >
            {t.common.close}
          </button>
        </div>
      )}

      <div className="dn-corr-layout">
        <section className="dn-card dn-corr-panel dn-corr-recipients" aria-labelledby="dn-cert-recipients-heading">
          <div className="dn-corr-panel-head">
            <h2 id="dn-cert-recipients-heading">{t.admin.certRecipientsHeading}</h2>
            <span className="dn-muted">
              {selected.length
                ? t.admin.certSelectedCount.replace("{count}", String(selected.length))
                : t.admin.certSelectedNone}
            </span>
          </div>

          <label className="dn-corr-field">
            <span>
              {t.admin.certSearchRecipients}
              <abbr className="dn-corr-required" title={t.admin.certRequired}>
                *
              </abbr>
            </span>
            <input
              type="search"
              value={query}
              onChange={(ev) => setQuery(ev.target.value)}
              placeholder={t.admin.certSearchPlaceholder}
              disabled={busy}
              aria-invalid={Boolean(fieldErrors.recipients)}
            />
            {fieldErrors.recipients && (
              <span className="dn-corr-field-error">{fieldErrors.recipients}</span>
            )}
          </label>

          {selected.length > 0 && (
            <ul
              className="dn-corr-chips"
              aria-label={t.admin.certSelectedCount.replace("{count}", String(selected.length))}
            >
              {selected.map((s) => (
                <li key={s.user_id}>
                  <button
                    type="button"
                    className="dn-corr-chip is-selected"
                    disabled={busy}
                    onClick={() => removeSelected(s.user_id)}
                    title={t.admin.certRemoveRecipient}
                  >
                    <span className="dn-corr-chip-name">{s.name}</span>
                    <span className="dn-badge">{typeLabel(t, s.recipient_type)}</span>
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="dn-table-wrap dn-corr-recipient-table">
            <table className="dn-table dn-table-responsive">
              <thead>
                <tr>
                  <th scope="col" className="dn-corr-check-col">
                    <span className="dn-sr-only">{t.admin.certSelect}</span>
                  </th>
                  <th scope="col">{t.admin.certRecipient}</th>
                  <th scope="col">Email</th>
                  <th scope="col">{t.admin.certType}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((row) => {
                  const checked = selectedIds.has(row.user_id);
                  return (
                    <tr
                      key={row.user_id}
                      className={checked ? "dn-corr-row-selected" : undefined}
                      onClick={() => !busy && toggle(row)}
                    >
                      <td className="dn-corr-check-col" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => toggle(row)}
                          aria-label={row.name}
                        />
                      </td>
                      <td>
                        <strong>{row.name}</strong>
                        {row.title_or_role ? (
                          <div className="dn-muted" style={{ fontSize: "0.85em" }}>
                            {row.title_or_role}
                          </div>
                        ) : null}
                      </td>
                      <td className="dn-corr-email">{row.email}</td>
                      <td>
                        <span className="dn-badge">{typeLabel(t, row.recipient_type)}</span>
                      </td>
                    </tr>
                  );
                })}
                {!results.length && (
                  <tr>
                    <td colSpan={4} className="dn-muted">
                      {t.admin.certNoRecipients}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="dn-card dn-corr-panel dn-corr-compose" aria-labelledby="dn-cert-options-heading">
          <div className="dn-corr-panel-head">
            <h2 id="dn-cert-options-heading">{t.admin.certOptionsHeading}</h2>
          </div>

          <label className="dn-corr-field">
            <span>{t.admin.certLanguage}</span>
            <select
              value={language}
              onChange={(ev) => setLanguage(ev.target.value as typeof language)}
              disabled={busy}
            >
              <option value="auto">{t.admin.certLanguageAuto}</option>
              <option value="en">{t.admin.certLanguageEn}</option>
              <option value="ar">{t.admin.certLanguageAr}</option>
              <option value="fr">{t.admin.certLanguageFr}</option>
            </select>
          </label>

          {selected.length > 0 && (
            <div className="dn-corr-field">
              <span>{t.admin.certPersonalize}</span>
              <p className="dn-muted" style={{ marginTop: 4, marginBottom: 10, fontSize: "0.9em" }}>
                {t.admin.certPersonalizeLead}
              </p>
              {selected.map((s) => {
                const draft = drafts[s.user_id] || draftFromRecipient(s);
                const presetValues = HONORIFIC_OPTIONS.map((o) => o.value).filter(
                  (v) => v && v !== "__custom__",
                );
                const isCustom =
                  draft.honorific !== "" && !presetValues.includes(draft.honorific);
                const selectValue = isCustom ? "__custom__" : draft.honorific;
                return (
                  <fieldset
                    key={s.user_id}
                    style={{
                      border: "1px solid var(--dn-border, #ddd)",
                      borderRadius: 8,
                      padding: "12px 14px",
                      marginBottom: 12,
                    }}
                  >
                    <legend style={{ padding: "0 6px", fontWeight: 600 }}>{s.name}</legend>
                    <label className="dn-corr-field">
                      <span>{t.admin.certHonorific}</span>
                      <select
                        value={selectValue}
                        disabled={busy}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          if (v === "__custom__") {
                            updateDraft(s.user_id, { honorific: draft.honorific || "" });
                          } else {
                            updateDraft(s.user_id, { honorific: v });
                          }
                        }}
                      >
                        {HONORIFIC_OPTIONS.map((opt) => (
                          <option key={opt.value || "none"} value={opt.value}>
                            {t.admin[opt.labelKey]}
                          </option>
                        ))}
                      </select>
                    </label>
                    {(selectValue === "__custom__" || isCustom) && (
                      <label className="dn-corr-field">
                        <span>{t.admin.certHonorificCustomValue}</span>
                        <input
                          type="text"
                          value={draft.honorific === "__custom__" ? "" : draft.honorific}
                          disabled={busy}
                          dir="auto"
                          onChange={(ev) => updateDraft(s.user_id, { honorific: ev.target.value })}
                        />
                      </label>
                    )}
                    <label className="dn-corr-field">
                      <span>{t.admin.certRecipientName}</span>
                      <input
                        type="text"
                        value={draft.recipient_name}
                        disabled={busy}
                        dir="auto"
                        onChange={(ev) => updateDraft(s.user_id, { recipient_name: ev.target.value })}
                      />
                    </label>
                    <label className="dn-corr-field">
                      <span>{t.admin.certAppreciation}</span>
                      <textarea
                        rows={2}
                        value={draft.appreciation}
                        disabled={busy}
                        dir="auto"
                        placeholder={t.admin.certAppreciationPlaceholder}
                        onChange={(ev) => updateDraft(s.user_id, { appreciation: ev.target.value })}
                      />
                    </label>
                    <label className="dn-corr-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                      <input
                        type="radio"
                        name="cert-preview-target"
                        checked={previewTargetId === s.user_id}
                        disabled={busy}
                        onChange={() => setPreviewTargetId(s.user_id)}
                      />
                      <span>{t.admin.certPreviewThisRecipient}</span>
                    </label>
                  </fieldset>
                );
              })}
            </div>
          )}

          <label className="dn-corr-field">
            <span>{t.admin.certNotes}</span>
            <textarea
              rows={4}
              value={notes}
              onChange={(ev) => setNotes(ev.target.value)}
              disabled={busy}
              dir="auto"
            />
          </label>

          <div className="dn-corr-actions">
            <button
              type="button"
              className="dn-btn dn-btn-ghost"
              disabled={busy}
              onClick={onPreview}
            >
              {previewing ? t.admin.certPreviewing : t.admin.certPreviewPdf}
            </button>
            <button
              type="button"
              className="dn-btn dn-btn-gold dn-corr-send"
              disabled={busy}
              aria-busy={sending}
              onClick={onSend}
            >
              {sending ? (
                <>
                  <span className="dn-corr-spinner" aria-hidden="true" />
                  {t.admin.certSending}
                </>
              ) : (
                t.admin.certSend
              )}
            </button>
            <Link to="/admin/certificates/history" className="dn-btn dn-btn-ghost dn-corr-history-link">
              {t.admin.certHistory}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
